"use client";

import { Slot } from "@radix-ui/react-slot";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { ChevronsUpDown } from "lucide-react";
import { animate } from "motion/react";
import {
	type ButtonHTMLAttributes,
	forwardRef,
	type ReactNode,
	useEffect,
	useRef,
} from "react";
import { cn } from "../../../lib/cn";
import { PulseRing } from "../../pulse-ring";
import { GlassCircleChrome } from "../glass-panel";
import {
	CIRCLE_BUTTON_NOTIFY_PULSE_COLORS,
	CIRCLE_BUTTON_STATUS_ABSORB,
	type CircleButtonNotifyPulseColor,
} from "./circle-button-status-absorb";

/**
 * Circle control sizes:
 * - `xs` — dense tooltips / Signpost edit affordances (24px)
 * - `sm` — topdrawer / panel headers / SaaS header actions (32px)
 * - `md` — topic viewer top-right HUD (40px)
 * - `lg` — topic card prev/next chevrons (64px)
 * - `xl` — audio play/pause (128px)
 */
export const circleButtonSizeClassName = {
	xs: "size-6",
	sm: "size-8",
	md: "size-10",
	lg: "size-16",
	xl: "size-32",
} as const;

export const circleButtonIconSizeClassName = {
	xs: "size-3",
	sm: "size-4",
	md: "size-4",
	lg: "size-6",
	xl: "size-10",
} as const;

export type CircleButtonSize = keyof typeof circleButtonSizeClassName;

/** Glass plate vs solid disc (secondary gradient / header navy). */
export type CircleButtonChrome = "solid" | "glass";

/**
 * Color treatment:
 * - `system` — follows light/dark theme (default)
 * - `dark` — fixed navy HUD (legacy / marketing)
 * - `light` — fixed light plate
 */
export type CircleButtonTone = "system" | "dark" | "light";

/**
 * Isolate glyph SVGs on their own composited layer (pricing matrix pattern).
 * Prevents parent hover washes from subpixel-shifting strokes.
 */
export const CIRCLE_BUTTON_ICON_SVG_LAYER_CLASS =
	"pointer-events-none block shrink-0";

/**
 * Dead-center icon frame — absolute + 50% translate (not flex/grid content
 * sizing). Avoid extra `translate3d` here; it fights the centering translate.
 */
export const CIRCLE_BUTTON_ICON_CENTER_CLASS =
	"pointer-events-none absolute left-1/2 top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2";

/**
 * Fixed icon frame + compositor-stable SVG. Prefer absolute `[&>svg]:!size-*`
 * over `size-full` — percentage size fights lucide/heroicon width/height attrs.
 */
export const CIRCLE_BUTTON_ICON_BOX_LAYER_CLASS = cn(
	CIRCLE_BUTTON_ICON_CENTER_CLASS,
	"grid place-items-center overflow-hidden leading-[0]",
);

/**
 * Smooth bg highlight via a `::before` wash — not CSS `filter`/`brightness`
 * (those re-raster SVG strokes) and not bare `hover:bg-*` (invisible on
 * gradient solid discs where `background-image` covers `background-color`).
 *
 * Do **not** use `enabled:hover:` — that pseudo only matches form controls, so
 * `asChild` Links (e.g. header Settings) would never get the wash.
 */
export const CIRCLE_BUTTON_HOVER_WASH_CLASS = cn(
	"before:pointer-events-none before:absolute before:inset-0 before:z-0 before:rounded-full",
	"before:bg-transparent before:transition-[background-color] before:duration-200 before:ease-out",
	"hover:before:bg-black/10 dark:hover:before:bg-white/15",
	"disabled:hover:before:bg-transparent aria-disabled:hover:before:bg-transparent",
);

/** Theme-adaptive secondary gradient disc (non-glass). */
export const CIRCLE_BUTTON_SOLID_SYSTEM_SURFACE_CLASS = cn(
	"bg-gradient-to-b from-white via-neutral-50 to-neutral-100 text-neutral-900",
	"dark:from-[#243652] dark:via-[#1a2435] dark:to-[#0f1419] dark:text-white",
	"shadow-[0_1px_2px_rgb(15_23_42/0.08),0_3px_10px_rgb(15_23_42/0.06)]",
	"ring-1 ring-inset ring-white/50 dark:ring-white/12",
);

/** Fixed navy HUD disc (legacy). Prefer `chrome="glass"` for SaaS header. */
export const CIRCLE_BUTTON_SOLID_DARK_SURFACE_CLASS = cn(
	"border-2 border-solid border-black bg-[#1a1d23] text-white shadow-md",
	"hover:bg-[#252a32] hover:text-white",
);

/** Fixed light disc. */
export const CIRCLE_BUTTON_SOLID_LIGHT_SURFACE_CLASS = cn(
	"border border-black/10 bg-white text-neutral-900 shadow-md",
	"hover:bg-neutral-50",
);

const solidSurfaceByTone: Record<CircleButtonTone, string> = {
	system: CIRCLE_BUTTON_SOLID_SYSTEM_SURFACE_CLASS,
	dark: CIRCLE_BUTTON_SOLID_DARK_SURFACE_CLASS,
	light: CIRCLE_BUTTON_SOLID_LIGHT_SURFACE_CLASS,
};

const circleButtonInteractionClassName = cn(
	"transition-[opacity,background-color,color,box-shadow] duration-200",
	"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
	/**
	 * Plain `cursor-pointer` (not `enabled:`) so `asChild` anchors get it —
	 * `:enabled` only matches form elements.
	 */
	"cursor-pointer",
	"disabled:cursor-not-allowed disabled:opacity-40",
	"aria-disabled:cursor-not-allowed aria-disabled:opacity-40",
);

const circleButtonSolidVariants = cva(
	cn(
		"relative shrink-0 overflow-hidden rounded-full leading-[0]",
		circleButtonInteractionClassName,
	),
	{
		variants: {
			size: circleButtonSizeClassName,
			tone: solidSurfaceByTone,
		},
		defaultVariants: {
			size: "sm",
			tone: "system",
		},
	},
);

const circleButtonInnerClassName = cn(
	"relative size-full overflow-hidden rounded-full leading-[0]",
	circleButtonInteractionClassName,
	CIRCLE_BUTTON_HOVER_WASH_CLASS,
);

/**
 * Absolute SVG size tokens per variant — forced with `!` so call-site
 * `className="size-*"` / intrinsic width/height attrs cannot win.
 * Direct-child only (`[&>svg]`) so composite widgets (e.g. metered ring) keep
 * their own sizing.
 */
const iconSvgAbsoluteSizeClassName: Record<CircleButtonSize, string> = {
	xs: "[&>svg]:!size-3",
	sm: "[&>svg]:!size-4",
	md: "[&>svg]:!size-4",
	lg: "[&>svg]:!size-6",
	xl: "[&>svg]:!size-10",
};

function circleButtonIconBoxClassName(
	size: CircleButtonSize,
	dropdownCaret = false,
): string {
	return cn(
		dropdownCaret
			? "pointer-events-none absolute top-1/2 left-1.5 z-[1] grid -translate-y-1/2 place-items-center overflow-hidden leading-[0]"
			: CIRCLE_BUTTON_ICON_BOX_LAYER_CLASS,
		circleButtonIconSizeClassName[size],
		iconSvgAbsoluteSizeClassName[size],
		"[&>svg]:pointer-events-none [&>svg]:block [&>svg]:shrink-0",
	);
}

/**
 * `asChild` (e.g. Link): pin the SVG itself to the disc center — no icon box.
 */
const iconSvgSizeClassName: Record<CircleButtonSize, string> = {
	xs: cn(
		"[&>svg]:absolute [&>svg]:left-1/2 [&>svg]:top-1/2",
		"[&>svg]:-translate-x-1/2 [&>svg]:-translate-y-1/2",
		"[&>svg]:!size-3 [&>svg]:block [&>svg]:shrink-0",
	),
	sm: cn(
		"[&>svg]:absolute [&>svg]:left-1/2 [&>svg]:top-1/2",
		"[&>svg]:-translate-x-1/2 [&>svg]:-translate-y-1/2",
		"[&>svg]:!size-4 [&>svg]:block [&>svg]:shrink-0",
	),
	md: cn(
		"[&>svg]:absolute [&>svg]:left-1/2 [&>svg]:top-1/2",
		"[&>svg]:-translate-x-1/2 [&>svg]:-translate-y-1/2",
		"[&>svg]:!size-4 [&>svg]:block [&>svg]:shrink-0",
	),
	lg: cn(
		"[&>svg]:absolute [&>svg]:left-1/2 [&>svg]:top-1/2",
		"[&>svg]:-translate-x-1/2 [&>svg]:-translate-y-1/2",
		"[&>svg]:!size-6 [&>svg]:block [&>svg]:shrink-0",
	),
	xl: cn(
		"[&>svg]:absolute [&>svg]:left-1/2 [&>svg]:top-1/2",
		"[&>svg]:-translate-x-1/2 [&>svg]:-translate-y-1/2",
		"[&>svg]:!size-10 [&>svg]:block [&>svg]:shrink-0",
	),
};

/** Capsule width when {@link CircleButtonProps.dropdownCaret} is set (sm/md). */
const dropdownCaretShellClassName: Partial<Record<CircleButtonSize, string>> = {
	sm: "h-8 w-auto min-w-11 pl-1.5 pr-5",
	md: "h-10 w-auto min-w-12 pl-2 pr-6",
};

/** Diagonal strike (captions / narrator off) — absolute line across the disc. */
const strikethroughClassName = cn(
	"pointer-events-none absolute inset-0 z-[2] overflow-hidden rounded-full",
	"before:absolute before:top-1/2 before:left-[12%] before:right-[12%] before:h-0.5",
	"before:-translate-y-1/2 before:rotate-[-45deg] before:rounded-full",
	"before:bg-current before:opacity-80 before:content-['']",
);

export interface CircleButtonNotifyDotProps {
	className?: string;
	/** Pulse ring emerging from the dot (header notifications / jobs). */
	pulse?: boolean;
	/** Pulse ring color. @default "red" */
	pulseColor?: CircleButtonNotifyPulseColor;
	/** Rim color behind the red/amber dot — match the disc fill. */
	tone?: CircleButtonTone;
}

/**
 * Notification badge for {@link CircleButton} — same geometry as legacy
 * HeaderNotifyDot (`right-1 top-1`, translate half out).
 */
export function CircleButtonNotifyDot({
	className,
	pulse = false,
	pulseColor = "red",
	tone = "dark",
}: CircleButtonNotifyDotProps) {
	const ringClass =
		tone === "dark"
			? "ring-[#1a1d23]"
			: tone === "light"
				? "ring-white"
				: "ring-background";
	const dotClass =
		pulseColor === "amber"
			? "bg-amber-500"
			: pulseColor === "green"
				? "bg-emerald-500"
				: "bg-red-500";
	const borderColor = CIRCLE_BUTTON_NOTIFY_PULSE_COLORS[pulseColor];

	return (
		<span
			className={cn(
				"pointer-events-none absolute top-1 right-1 z-20",
				"translate-x-1/2 -translate-y-1/2",
				"inline-flex size-2 items-center justify-center rounded-full",
				dotClass,
				"ring-2",
				ringClass,
				className,
			)}
			aria-hidden
		>
			{pulse ? (
				<PulseRing
					className="-z-10"
					sizePx={20}
					borderPx={2}
					borderColor={borderColor}
					durationS={2.4}
				/>
			) : null}
		</span>
	);
}

export interface CircleButtonProps
	extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">,
		VariantProps<typeof circleButtonSolidVariants> {
	/** Solid disc vs glass plate. @default "solid" */
	chrome?: CircleButtonChrome;
	/**
	 * Icon or control content. Prefer Lucide icons without a size class —
	 * size is enforced via a fixed icon box ({@link circleButtonIconSizeClassName}).
	 */
	icon?: ReactNode;
	/** Alias for {@link icon} when not using `asChild`. */
	children?: ReactNode;
	/** Active / on — primary focus ring. */
	pressed?: boolean;
	/** Diagonal strike (captions / narrator off). */
	showStrikethrough?: boolean;
	/** Show notification dot (header-style). */
	notifyDot?: boolean;
	/** Pulse ring on the notify dot. */
	notifyPulse?: boolean;
	/** Pulse ring / dot color. @default "red" */
	notifyPulseColor?: CircleButtonNotifyPulseColor;
	/**
	 * When this key changes, play a one-shot scale absorb (1 → 1.08 → 1).
	 * Shared for jobs / notifications / usage status edges.
	 */
	statusAbsorbKey?: string | number;
	/**
	 * Menu / dropdown trigger: capsule shell + ChevronsUpDown on the right.
	 * Icon size tokens still apply; caret uses a fixed smaller size.
	 */
	dropdownCaret?: boolean;
	/** Render as child slot (e.g. `Link`). */
	asChild?: boolean;
	/** Extra class on the glass outer shell when `chrome="glass"`. */
	glassClassName?: string;
	/**
	 * Skip the fixed icon box — center content in the full disc (metered usage
	 * ring, custom glyphs). Default false pins Lucide/Heroicons to size tokens.
	 */
	fitContent?: boolean;
}

/**
 * Shared circular icon control — topdrawer, topic HUD, panel headers, SaaS
 * header, transport. Prefer this over ad-hoc `rounded-full` buttons.
 */
export const CircleButton = forwardRef<HTMLButtonElement, CircleButtonProps>(
	function CircleButton(
		{
			chrome = "solid",
			tone = "system",
			size = "sm",
			icon,
			children,
			pressed = false,
			showStrikethrough = false,
			notifyDot = false,
			notifyPulse = false,
			notifyPulseColor = "red",
			statusAbsorbKey,
			dropdownCaret = false,
			disabled = false,
			asChild = false,
			className,
			glassClassName,
			fitContent = false,
			type = "button",
			...props
		},
		ref,
	) {
		const resolvedSize = size ?? "sm";
		const resolvedTone = tone ?? "system";
		const content = icon ?? children;
		const sizeClass = dropdownCaret
			? (dropdownCaretShellClassName[resolvedSize] ??
				circleButtonSizeClassName[resolvedSize])
			: circleButtonSizeClassName[resolvedSize];
		const showNotify = notifyDot || notifyPulse;
		const absorbShellRef = useRef<HTMLSpanElement>(null);
		const previousAbsorbKeyRef = useRef<string | number | undefined>(
			undefined,
		);

		useEffect(() => {
			if (statusAbsorbKey === undefined) {
				return;
			}
			if (previousAbsorbKeyRef.current === undefined) {
				previousAbsorbKeyRef.current = statusAbsorbKey;
				return;
			}
			if (previousAbsorbKeyRef.current === statusAbsorbKey) {
				return;
			}
			previousAbsorbKeyRef.current = statusAbsorbKey;
			const el = absorbShellRef.current;
			if (!el) {
				return;
			}
			el.style.willChange = "transform";
			const controls = animate(
				el,
				{
					scale: [
						CIRCLE_BUTTON_STATUS_ABSORB.from,
						CIRCLE_BUTTON_STATUS_ABSORB.peak,
						CIRCLE_BUTTON_STATUS_ABSORB.to,
					],
				},
				{
					duration: CIRCLE_BUTTON_STATUS_ABSORB.durationMs / 1000,
					ease: "easeOut",
				},
			);
			const clearMotionHints = () => {
				el.style.willChange = "";
				el.style.transform = "";
			};
			const clearTimer = window.setTimeout(
				clearMotionHints,
				CIRCLE_BUTTON_STATUS_ABSORB.durationMs + 32,
			);
			return () => {
				window.clearTimeout(clearTimer);
				controls.stop();
				clearMotionHints();
			};
		}, [statusAbsorbKey]);

		const notify = showNotify ? (
			<CircleButtonNotifyDot
				pulse={notifyPulse}
				pulseColor={notifyPulseColor}
				tone={resolvedTone}
			/>
		) : null;

		const strike = showStrikethrough ? (
			<span aria-hidden className={strikethroughClassName} />
		) : null;

		const pressedClass = pressed
			? "ring-2 ring-primary/70 ring-offset-1 ring-offset-background"
			: undefined;

		const caret =
			dropdownCaret && !asChild ? (
				<ChevronsUpDown
					data-circle-button-caret=""
					className="pointer-events-none absolute top-1/2 right-1 z-[1] !size-3 -translate-y-1/2 shrink-0 opacity-80"
					aria-hidden
				/>
			) : null;

		const labeledContent = fitContent ? (
			<span className="pointer-events-none absolute inset-0 z-[1] overflow-hidden">
				{content}
			</span>
		) : (
			<span
				className={circleButtonIconBoxClassName(
					resolvedSize,
					dropdownCaret,
				)}
			>
				{content}
			</span>
		);

		const wrapAbsorb = (node: ReactNode) => {
			if (statusAbsorbKey === undefined) {
				return node;
			}
			return (
				<span
					ref={absorbShellRef}
					className="relative inline-flex shrink-0 origin-center"
				>
					{node}
				</span>
			);
		};

		/**
		 * `asChild` merges onto a single element (e.g. `Link`). Notify/strike/caret are
		 * not injected — wrap externally if needed.
		 */
		if (asChild) {
			if (chrome === "glass") {
				return wrapAbsorb(
					<span
						className={cn(
							"relative inline-flex shrink-0 overflow-visible",
							className,
						)}
					>
						<GlassCircleChrome
							sizeClassName={sizeClass}
							tone={resolvedTone}
							className={glassClassName}
						>
							<Slot
								ref={ref}
								aria-disabled={disabled || undefined}
								aria-pressed={pressed}
								className={cn(
									circleButtonInnerClassName,
									pressedClass,
									iconSvgSizeClassName[resolvedSize],
								)}
								{...props}
							>
								{children}
							</Slot>
						</GlassCircleChrome>
						{notify}
					</span>,
				);
			}

			return wrapAbsorb(
				<span
					className={cn(
						"relative inline-flex shrink-0",
						showNotify && "overflow-visible",
					)}
				>
					<Slot
						ref={ref}
						aria-disabled={disabled || undefined}
						aria-pressed={pressed}
						className={cn(
							circleButtonSolidVariants({
								size: resolvedSize,
								tone: resolvedTone,
							}),
							dropdownCaret && sizeClass,
							pressedClass,
							resolvedTone === "system" &&
								CIRCLE_BUTTON_HOVER_WASH_CLASS,
							iconSvgSizeClassName[resolvedSize],
							className,
						)}
						{...props}
					>
						{children}
					</Slot>
					{notify}
				</span>,
			);
		}

		if (chrome === "glass") {
			/**
			 * Notify sits outside {@link GlassCircleChrome} so the glass content
			 * can keep overflow-hidden (required for rounded-full clip) without
			 * clipping the pulse ring.
			 *
			 * `className` is on the outer span so layout/visibility utilities
			 * (e.g. `md:hidden`) hide the glass shell, not only the button.
			 *
			 * Hover wash lives on the inner button (`CIRCLE_BUTTON_HOVER_WASH_CLASS`)
			 * — same as the committed glass path / usage tray.
			 */
			return wrapAbsorb(
				<span
					className={cn(
						"relative inline-flex shrink-0 overflow-visible",
						className,
					)}
				>
					<GlassCircleChrome
						sizeClassName={sizeClass}
						tone={resolvedTone}
						className={glassClassName}
					>
						<button
							ref={ref}
							type={type}
							disabled={disabled}
							aria-disabled={disabled || undefined}
							aria-pressed={pressed}
							aria-haspopup={dropdownCaret ? "menu" : undefined}
							className={cn(
								circleButtonInnerClassName,
								pressedClass,
							)}
							{...props}
						>
							{labeledContent}
							{caret}
							{strike}
						</button>
					</GlassCircleChrome>
					{notify}
				</span>,
			);
		}

		return wrapAbsorb(
			<button
				ref={ref}
				type={type}
				disabled={disabled}
				aria-disabled={disabled || undefined}
				aria-pressed={pressed}
				aria-haspopup={dropdownCaret ? "menu" : undefined}
				className={cn(
					circleButtonSolidVariants({
						size: resolvedSize,
						tone: resolvedTone,
					}),
					dropdownCaret && sizeClass,
					pressedClass,
					showNotify && "overflow-visible",
					resolvedTone === "system" && CIRCLE_BUTTON_HOVER_WASH_CLASS,
					className,
				)}
				{...props}
			>
				{labeledContent}
				{caret}
				{strike}
				{notify}
			</button>,
		);
	},
);

CircleButton.displayName = "CircleButton";
